import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy, Plus, Loader2, UploadCloud, Users, Vote, ScrollText, BarChart3,
  CheckCircle2, AlertTriangle, Crown, Play, Square, ArrowRight, Download, X, Image as ImageIcon,
} from "lucide-react";
import { authHeaders, formatApiError, getUser } from "../../lib/admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full rounded-xl border border-brand/15 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500";

const EVENT_STATUTS = { brouillon: "Brouillon", publie: "Publié", suspendu: "Suspendu", archive: "Archivé" };
const EVENT_STYLE = {
  brouillon: "bg-slate-100 text-slate-600",
  publie: "bg-brand text-white",
  suspendu: "bg-gold-glow text-gold",
  archive: "bg-slate-200 text-slate-400",
};
const CAND_STATUTS = { en_attente: "En attente", validee: "Validée", rejetee: "Rejetée", a_corriger: "À corriger" };
const CAND_STYLE = {
  en_attente: "bg-gold-glow text-gold",
  validee: "bg-brand-light text-brand",
  rejetee: "bg-red-50 text-crimson",
  a_corriger: "bg-orange-50 text-orange-600",
};
const PHASE_STATUTS = { a_venir: "À venir", ouverte: "Votes ouverts", cloturee: "Clôturée", publiee: "Résultats publiés" };

const fmtDt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const toLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  } catch { return ""; }
};
const toIso = (local) => (local ? new Date(local).toISOString() : "");

const api = {
  get: (p) => axios.get(`${API}${p}`, { headers: authHeaders() }),
  post: (p, b) => axios.post(`${API}${p}`, b ?? {}, { headers: authHeaders() }),
  put: (p, b) => axios.put(`${API}${p}`, b, { headers: authHeaders() }),
};
const onErr = (e) => toast.error(formatApiError(e.response?.data?.detail));

const Badge = ({ style, children, testid }) => (
  <span data-testid={testid} className={`inline-flex rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${style}`}>{children}</span>
);

// ---------- Sous-onglet : Événement ----------
const EventTab = ({ detail, reload, reloadList, isAdmin }) => {
  const [form, setForm] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (detail) {
      setForm({
        nom: detail.nom, description: detail.description || "", reglement: detail.reglement || "",
        conditions: detail.conditions || "", prix_montant: detail.prix_montant || 0,
        prix_devise: detail.prix_devise || "FCFA",
        date_debut: toLocal(detail.date_debut), date_fin: toLocal(detail.date_fin),
        date_fin_inscription: toLocal(detail.date_fin_inscription),
      });
      setCreating(false);
    } else {
      setCreating(true);
      setForm({ nom: "", description: "", reglement: "", conditions: "", prix_montant: 500000, prix_devise: "FCFA", date_debut: "", date_fin: "", date_fin_inscription: "" });
    }
  }, [detail]);

  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      prix_montant: parseInt(form.prix_montant, 10) || 0,
      date_debut: toIso(form.date_debut), date_fin: toIso(form.date_fin),
      date_fin_inscription: toIso(form.date_fin_inscription),
    };
    try {
      if (creating) {
        const res = await api.post("/admin/concours", payload);
        toast.success("Événement créé avec ses 4 phases par défaut");
        await reloadList(res.data.id);
      } else {
        await api.put(`/admin/concours/${detail.id}`, payload);
        toast.success("Événement mis à jour");
        reload();
      }
    } catch (e) { onErr(e); } finally { setSaving(false); }
  };

  const setStatut = async (statut) => {
    try {
      await api.post(`/admin/concours/${detail.id}/statut`, { statut });
      toast.success(`Statut : ${EVENT_STATUTS[statut]}`);
      reload(); reloadList();
    } catch (e) { onErr(e); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const data = new FormData();
    data.append("fichier", file);
    try {
      await axios.post(`${API}/admin/concours/${detail.id}/upload?kind=${kind}`, data, { headers: authHeaders() });
      toast.success("Image mise à jour");
      reload();
    } catch (e) { onErr(e); }
  };

  return (
    <div className="space-y-6">
      {!creating && detail && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-brand/10 bg-white p-5">
          <Badge style={EVENT_STYLE[detail.statut]} testid="event-statut">{EVENT_STATUTS[detail.statut]}</Badge>
          <span className="text-sm text-slate-500">/{detail.slug}</span>
          {isAdmin && (
            <div className="ml-auto flex flex-wrap gap-2">
              {detail.statut !== "publie" && (
                <button onClick={() => setStatut("publie")} data-testid="event-publish-btn" className="rounded-full bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover">Publier</button>
              )}
              {detail.statut === "publie" && (
                <button onClick={() => setStatut("suspendu")} data-testid="event-suspend-btn" className="rounded-full bg-gold px-4 py-2 text-xs font-bold text-brand-deep hover:bg-gold-warm">Suspendre</button>
              )}
              {detail.statut !== "archive" && (
                <button onClick={() => setStatut("archive")} data-testid="event-archive-btn" className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500 hover:border-crimson hover:text-crimson">Archiver</button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-5 rounded-2xl border border-brand/10 bg-white p-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelCls}>Nom de l'événement *</label>
          <input data-testid="event-nom" value={form.nom} onChange={set("nom")} className={inputCls} placeholder="Ex : Miss Beauté Togo 2026" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Description</label>
          <textarea data-testid="event-description" rows={2} value={form.description} onChange={set("description")} className={`${inputCls} resize-y`} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Règlement</label>
          <textarea data-testid="event-reglement" rows={3} value={form.reglement} onChange={set("reglement")} className={`${inputCls} resize-y`} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Conditions de participation</label>
          <textarea data-testid="event-conditions" rows={2} value={form.conditions} onChange={set("conditions")} className={`${inputCls} resize-y`} />
        </div>
        <div>
          <label className={labelCls}>Montant du prix</label>
          <input data-testid="event-prix" type="number" min={0} value={form.prix_montant} onChange={set("prix_montant")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Devise</label>
          <input data-testid="event-devise" value={form.prix_devise} onChange={set("prix_devise")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Début de l'événement</label>
          <input data-testid="event-date-debut" type="datetime-local" value={form.date_debut} onChange={set("date_debut")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Fin de l'événement</label>
          <input data-testid="event-date-fin" type="datetime-local" value={form.date_fin} onChange={set("date_fin")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Clôture des inscriptions</label>
          <input data-testid="event-date-inscription" type="datetime-local" value={form.date_fin_inscription} onChange={set("date_fin_inscription")} className={inputCls} />
        </div>
        <div className="flex items-end">
          <button onClick={save} disabled={saving || !isAdmin} data-testid="event-save-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {creating ? "Créer l'événement" : "Enregistrer"}
          </button>
        </div>
      </div>
      {!creating && detail && isAdmin && (
        <div className="grid grid-cols-1 gap-5 rounded-2xl border border-brand/10 bg-white p-6 sm:grid-cols-2">
          {["logo", "cover"].map((kind) => (
            <ImageUpload key={kind} kind={kind} current={kind === "logo" ? detail.logo_url : detail.cover_url} onUpload={upload} />
          ))}
        </div>
      )}
    </div>
  );
};

const ImageUpload = ({ kind, current, onUpload }) => {
  const ref = useRef(null);
  const labels = { logo: "Logo du concours", cover: "Image de couverture", banniere: "Image de la bannière" };
  return (
    <div>
      <span className={labelCls}>{labels[kind]}</span>
      <div className="flex items-center gap-4">
        {current ? (
          <img src={`${BACKEND}${current}`} alt={kind} data-testid={`upload-preview-${kind}`} className="h-16 w-24 rounded-xl border border-brand/10 object-cover" />
        ) : (
          <div className="grid h-16 w-24 place-items-center rounded-xl border border-dashed border-brand/25 text-slate-300"><ImageIcon className="h-6 w-6" /></div>
        )}
        <button onClick={() => ref.current?.click()} data-testid={`upload-btn-${kind}`}
          className="inline-flex items-center gap-2 rounded-full border border-brand/25 px-4 py-2 text-xs font-bold text-brand hover:bg-brand-light">
          <UploadCloud className="h-4 w-4" /> Choisir une image
        </button>
        <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid={`upload-input-${kind}`}
          onChange={(e) => { if (e.target.files[0]) onUpload(kind, e.target.files[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
};

// ---------- Sous-onglet : Bannière & votes ----------
const ConfigTab = ({ detail, reload, isAdmin }) => {
  const [b, setB] = useState(detail.banniere || {});
  const [vc, setVc] = useState(detail.vote_config || {});
  useEffect(() => { setB({ ...detail.banniere, publication_at: toLocal(detail.banniere?.publication_at) }); setVc(detail.vote_config); }, [detail]);

  const saveBanniere = async () => {
    try {
      await api.put(`/admin/concours/${detail.id}/banniere`, { ...b, publication_at: toIso(b.publication_at) });
      toast.success("Bannière enregistrée");
      reload();
    } catch (e) { onErr(e); }
  };
  const saveVoteConfig = async () => {
    try {
      await api.put(`/admin/concours/${detail.id}/vote-config`, {
        affichage: vc.affichage || "live",
        votes_par_email_candidat_phase: parseInt(vc.votes_par_email_candidat_phase, 10) || 1,
        votes_par_email_phase: parseInt(vc.votes_par_email_phase, 10) || 20,
        otp_validite_minutes: parseInt(vc.otp_validite_minutes, 10) || 10,
        otp_max_demandes_heure: parseInt(vc.otp_max_demandes_heure, 10) || 3,
        otp_max_demandes_ip_heure: parseInt(vc.otp_max_demandes_ip_heure, 10) || 10,
        otp_max_tentatives: parseInt(vc.otp_max_tentatives, 10) || 5,
      });
      toast.success("Règles de vote enregistrées");
      reload();
    } catch (e) { onErr(e); }
  };
  const upload = async (kind, file) => {
    const data = new FormData();
    data.append("fichier", file);
    try {
      await axios.post(`${API}/admin/concours/${detail.id}/upload?kind=${kind}`, data, { headers: authHeaders() });
      toast.success("Image de bannière mise à jour");
      reload();
    } catch (e) { onErr(e); }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-brand/10 bg-white p-6">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><ScrollText className="h-5 w-5 text-brand" /> Bannière publique</h3>
        <div className="mt-5 space-y-4">
          <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-slate-700">
            <input type="checkbox" data-testid="banniere-active" checked={!!b.active} onChange={(e) => setB({ ...b, active: e.target.checked })} className="h-4 w-4 accent-[#0D482B]" />
            Bannière activée sur la page d'accueil
          </label>
          <div><label className={labelCls}>Titre</label><input data-testid="banniere-titre" value={b.titre || ""} onChange={(e) => setB({ ...b, titre: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Texte descriptif</label><textarea data-testid="banniere-texte" rows={2} value={b.texte || ""} onChange={(e) => setB({ ...b, texte: e.target.value })} className={`${inputCls} resize-y`} /></div>
          <div><label className={labelCls}>Libellé du bouton</label><input data-testid="banniere-bouton" value={b.bouton_label || ""} onChange={(e) => setB({ ...b, bouton_label: e.target.value })} className={inputCls} /></div>
          <div>
            <label className={labelCls}>Publication programmée (optionnel)</label>
            <input data-testid="banniere-publication" type="datetime-local" value={b.publication_at || ""} onChange={(e) => setB({ ...b, publication_at: e.target.value })} className={inputCls} />
            <p className="mt-1 text-xs text-slate-400">Si renseigné, la bannière n'apparaît qu'à partir de cette date.</p>
          </div>
          <ImageUpload kind="banniere" current={detail.banniere_image_url} onUpload={upload} />
          <button onClick={saveBanniere} disabled={!isAdmin} data-testid="banniere-save"
            className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-50">
            <CheckCircle2 className="h-4 w-4" /> Enregistrer la bannière
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-brand/10 bg-white p-6">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Vote className="h-5 w-5 text-brand" /> Règles de vote</h3>
        <div className="mt-5 space-y-4">
          <p className="rounded-xl bg-brand-light/60 px-4 py-3 text-xs font-semibold text-brand">
            Vote 100% gratuit — aucun paiement, aucun compte. Chaque votant vérifie son adresse email par code (OTP). Son email n'est jamais affiché publiquement.
          </p>
          <div>
            <label className={labelCls}>Affichage des scores</label>
            <select data-testid="vote-affichage" value={vc.affichage || "live"} onChange={(e) => setVc({ ...vc, affichage: e.target.value })} className={inputCls}>
              <option value="live">Votes en direct (scores + classement visibles pendant la phase)</option>
              <option value="masque">Scores masqués jusqu'à la validation officielle</option>
            </select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><label className={labelCls}>Votes / email / candidate / phase</label><input data-testid="vote-max-email-cand" type="number" min={1} max={10} value={vc.votes_par_email_candidat_phase ?? 1} onChange={(e) => setVc({ ...vc, votes_par_email_candidat_phase: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Votes max / email / phase</label><input data-testid="vote-max-email-phase" type="number" min={1} max={100} value={vc.votes_par_email_phase ?? 20} onChange={(e) => setVc({ ...vc, votes_par_email_phase: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Validité du code (minutes)</label><input data-testid="otp-validite" type="number" min={5} max={60} value={vc.otp_validite_minutes ?? 10} onChange={(e) => setVc({ ...vc, otp_validite_minutes: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Tentatives de code max</label><input data-testid="otp-tentatives" type="number" min={3} max={10} value={vc.otp_max_tentatives ?? 5} onChange={(e) => setVc({ ...vc, otp_max_tentatives: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Demandes de code / email / heure</label><input data-testid="otp-demandes" type="number" min={1} max={20} value={vc.otp_max_demandes_heure ?? 3} onChange={(e) => setVc({ ...vc, otp_max_demandes_heure: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Demandes de code / réseau / heure</label><input data-testid="otp-demandes-ip" type="number" min={1} max={100} value={vc.otp_max_demandes_ip_heure ?? 10} onChange={(e) => setVc({ ...vc, otp_max_demandes_ip_heure: e.target.value })} className={inputCls} /></div>
          </div>
          <button onClick={saveVoteConfig} disabled={!isAdmin} data-testid="vote-config-save"
            className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-50">
            <CheckCircle2 className="h-4 w-4" /> Enregistrer les règles
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Sous-onglet : Candidatures ----------
const CandidaturesTab = ({ eventId, isAdmin }) => {
  const [list, setList] = useState(null);
  const [filtre, setFiltre] = useState("");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [numero, setNumero] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/concours/${eventId}/candidatures${filtre ? `?statut=${filtre}` : ""}`)
      .then((r) => setList(r.data)).catch(onErr);
  }, [eventId, filtre]);
  useEffect(load, [load]);

  const setStatut = async (cid, statut) => {
    try {
      await api.put(`/admin/concours/${eventId}/candidatures/${cid}/statut`, { statut, note });
      toast.success(`Candidature ${CAND_STATUTS[statut].toLowerCase()}`);
      setNote(""); setSel(null); load();
    } catch (e) { onErr(e); }
  };
  const saveNumero = async (cid, value) => {
    try {
      await api.put(`/admin/concours/${eventId}/candidatures/${cid}/numero`, { numero: value === "" ? null : parseInt(value, 10) });
      toast.success(value === "" ? "Numéro retiré" : `Numéro ${String(value).padStart(2, "0")} attribué`);
      setSel(null); load();
    } catch (e) { onErr(e); }
  };

  if (!list) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;
  const officielles = list.filter((c) => c.numero !== null).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Badge style="bg-brand-light text-brand" testid="officielles-count">{officielles} / 20 candidates officielles</Badge>
        <div className="ml-auto flex flex-wrap gap-2">
          {["", ...Object.keys(CAND_STATUTS)].map((s) => (
            <button key={s} onClick={() => setFiltre(s)} data-testid={`filter-${s || "toutes"}`}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${filtre === s ? "bg-brand text-white" : "bg-white text-slate-500 border border-brand/10"}`}>
              {s ? CAND_STATUTS[s] : "Toutes"}
            </button>
          ))}
        </div>
      </div>
      {list.length === 0 && <p className="rounded-2xl border border-dashed border-brand/20 bg-white p-10 text-center text-sm text-slate-400">Aucune candidature pour le moment.</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => (
          <button key={c.id} onClick={() => { setSel(c); setNote(c.note_admin || ""); setNumero(c.numero ?? ""); }}
            data-testid={`candidature-card-${c.id.slice(0, 8)}`}
            className="flex items-center gap-4 rounded-2xl border border-brand/10 bg-white p-4 text-left transition-shadow hover:shadow-md">
            {c.photo_url ? (
              <img src={`${BACKEND}${c.photo_url}`} alt="" className="h-16 w-16 rounded-xl object-cover" />
            ) : (
              <div className="grid h-16 w-16 place-items-center rounded-xl bg-brand-light text-brand"><Users className="h-6 w-6" /></div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {c.numero !== null && <span className="grid h-6 w-6 place-items-center rounded-full bg-brand-deep font-mono text-[10px] font-bold text-gold">{String(c.numero).padStart(2, "0")}</span>}
                <span className="truncate font-display text-sm font-bold text-slate-900">{c.prenom} {c.nom}</span>
              </div>
              <div className="mt-0.5 truncate text-xs text-slate-500">{c.ville} · {c.age} ans</div>
              <Badge style={CAND_STYLE[c.statut]}>{CAND_STATUTS[c.statut]}</Badge>
            </div>
          </button>
        ))}
      </div>

      {sel && (
        <div className="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-brand-deep/50 p-4 backdrop-blur-sm" onClick={() => setSel(null)}>
          <div data-testid="candidature-modal" className="my-8 w-full max-w-2xl rounded-3xl bg-white p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                {sel.photo_url && <img src={`${BACKEND}${sel.photo_url}`} alt="" data-testid="candidature-photo" className="h-24 w-24 rounded-2xl object-cover" />}
                <div>
                  <h3 className="font-display text-xl font-extrabold text-slate-900">{sel.prenom} {sel.nom}</h3>
                  <p className="text-xs text-slate-500">{sel.email} · {sel.telephone}</p>
                  <p className="text-xs text-slate-500">{sel.ville} · {sel.age} ans · {fmtDt(sel.created_at)}</p>
                  <Badge style={CAND_STYLE[sel.statut]}>{CAND_STATUTS[sel.statut]}</Badge>
                </div>
              </div>
              <button onClick={() => setSel(null)} data-testid="candidature-modal-close" className="rounded-full p-2 text-slate-400 hover:bg-slate-100" aria-label="Fermer"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><div className={labelCls}>Biographie</div><p className="text-sm text-slate-600">{sel.bio}</p></div>
              <div><div className={labelCls}>Motivation</div><p className="text-sm text-slate-600">{sel.motivation}</p></div>
            </div>
            {(sel.video_file_url || sel.video_url) && (
              <div className="mt-4">
                {sel.video_file_url ? (
                  <video src={`${BACKEND}${sel.video_file_url}`} controls playsInline preload="metadata" data-testid="candidature-video" className="w-full rounded-2xl bg-black" />
                ) : (
                  <a href={sel.video_url} target="_blank" rel="noopener noreferrer" data-testid="candidature-video-link"
                    className="flex items-center gap-2 rounded-xl border border-brand/20 bg-brand-light/50 px-4 py-3 text-sm font-semibold text-brand">
                    <Play className="h-4 w-4" /> Voir la vidéo <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
            <div className="mt-5">
              <label className={labelCls}>Note interne (envoyée par email à la candidate)</label>
              <textarea data-testid="candidature-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} resize-y`} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => setStatut(sel.id, "validee")} data-testid="candidature-valider" className="rounded-full bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover">Valider</button>
              <button onClick={() => setStatut(sel.id, "a_corriger")} data-testid="candidature-corriger" className="rounded-full bg-orange-500 px-4 py-2 text-xs font-bold text-white hover:bg-orange-600">À corriger</button>
              <button onClick={() => setStatut(sel.id, "rejetee")} data-testid="candidature-rejeter" className="rounded-full bg-crimson px-4 py-2 text-xs font-bold text-white hover:bg-red-700">Rejeter</button>
              {isAdmin && sel.statut === "validee" && (
                <span className="ml-auto flex items-center gap-2">
                  <input type="number" min={1} max={20} value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="N°"
                    data-testid="candidature-numero" className={`${inputCls} w-24`} />
                  <button onClick={() => saveNumero(sel.id, numero)} data-testid="candidature-numero-save"
                    className="rounded-full bg-gold px-4 py-2 text-xs font-bold text-brand-deep hover:bg-gold-warm">
                    {numero === "" ? "Retirer du concours" : "Attribuer le n°"}
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Sous-onglet : Phases ----------
const PhaseCard = ({ eventId, phase, reload, isAdmin }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [qualifiees, setQualifiees] = useState([]);
  const [note, setNote] = useState("");

  const startEdit = () => {
    setForm({
      nom: phase.nom, description: phase.description || "",
      date_debut: toLocal(phase.date_debut), date_fin: toLocal(phase.date_fin),
      nb_qualifies: phase.nb_qualifies, publication: phase.publication || "",
    });
    setEdit(true);
  };

  const save = async () => {
    try {
      await api.put(`/admin/concours/${eventId}/phases/${phase.id}`, {
        ...form, nb_qualifies: parseInt(form.nb_qualifies, 10) || 0,
        date_debut: toIso(form.date_debut), date_fin: toIso(form.date_fin),
      });
      toast.success("Phase mise à jour"); setEdit(false); reload();
    } catch (e) { onErr(e); }
  };

  const action = async (verb, body) => {
    try {
      const res = await api.post(`/admin/concours/${eventId}/phases/${phase.id}/${verb}`, body);
      if (verb === "cloturer" && res.data.egalites > 0) {
        toast.warning(`Phase clôturée — ${res.data.egalites} candidate(s) à égalité à la frontière de qualification`);
      } else {
        toast.success("Action effectuée");
      }
      reload();
    } catch (e) { onErr(e); }
  };

  const confirmer = () => action("confirmer", { qualifie_ids: qualifiees, note });

  const egalites = (phase.classement || []).filter((r) => r.egalite);

  return (
    <div data-testid={`admin-phase-${phase.ordre}`} className={`rounded-2xl border p-5 ${phase.statut === "ouverte" ? "border-gold/50 bg-gold-glow/20" : "border-brand/10 bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Badge style={phase.statut === "ouverte" ? "bg-brand text-white" : phase.statut === "publiee" ? "bg-gold text-brand-deep" : phase.statut === "cloturee" ? "bg-gold-glow text-gold" : "bg-slate-100 text-slate-500"} testid={`phase-statut-${phase.ordre}`}>
              {PHASE_STATUTS[phase.statut]}
            </Badge>
            <span className="text-xs text-slate-400">{phase.participants?.length || 0} candidates · {phase.nb_qualifies} qualifiée(s)</span>
          </div>
          <h4 className="mt-1.5 font-display text-lg font-bold text-slate-900">{phase.nom}</h4>
          <p className="text-xs text-slate-500">{fmtDt(phase.date_debut)} → {fmtDt(phase.date_fin)}</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {!["cloturee", "publiee"].includes(phase.statut) && (
              <button onClick={startEdit} data-testid={`phase-edit-${phase.ordre}`} className="rounded-full border border-brand/25 px-4 py-2 text-xs font-bold text-brand hover:bg-brand-light">Configurer</button>
            )}
            {phase.statut === "a_venir" && phase.type !== "proclamation" && (
              <button onClick={() => action("ouvrir")} data-testid={`phase-ouvrir-${phase.ordre}`} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover">
                <Play className="h-3.5 w-3.5" /> Ouvrir les votes
              </button>
            )}
            {phase.statut === "ouverte" && (
              <button onClick={() => action("cloturer")} data-testid={`phase-cloturer-${phase.ordre}`} className="inline-flex items-center gap-1.5 rounded-full bg-crimson px-4 py-2 text-xs font-bold text-white hover:bg-red-700">
                <Square className="h-3.5 w-3.5" /> Clôturer les votes
              </button>
            )}
            {phase.statut === "publiee" && phase.ordre < 4 && (
              <button onClick={() => action("lancer-suivante")} data-testid={`phase-suivante-${phase.ordre}`} className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-xs font-bold text-brand-deep hover:bg-gold-warm">
                Lancer la phase suivante <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {edit && form && (
        <div className="mt-4 grid grid-cols-1 gap-4 rounded-xl bg-ivory p-4 sm:grid-cols-2" data-testid={`phase-form-${phase.ordre}`}>
          <div className="sm:col-span-2"><label className={labelCls}>Nom</label><input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Début des votes</label><input type="datetime-local" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} className={inputCls} data-testid={`phase-debut-${phase.ordre}`} /></div>
          <div><label className={labelCls}>Fin des votes</label><input type="datetime-local" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} className={inputCls} data-testid={`phase-fin-${phase.ordre}`} /></div>
          <div><label className={labelCls}>Nombre de qualifiées</label><input type="number" min={0} value={form.nb_qualifies} onChange={(e) => setForm({ ...form, nb_qualifies: e.target.value })} className={inputCls} data-testid={`phase-qualifies-${phase.ordre}`} /></div>
          <div>
            <label className={labelCls}>Publication des scores</label>
            <select value={form.publication} onChange={(e) => setForm({ ...form, publication: e.target.value })} className={inputCls} data-testid={`phase-publication-${phase.ordre}`}>
              <option value="">Hérite de l'événement</option>
              <option value="live">Votes en direct</option>
              <option value="masque">Masqués jusqu'à validation</option>
            </select>
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <button onClick={save} className="rounded-full bg-brand px-5 py-2 text-xs font-bold text-white hover:bg-brand-hover" data-testid={`phase-save-${phase.ordre}`}>Enregistrer</button>
            <button onClick={() => setEdit(false)} className="rounded-full border border-slate-200 px-5 py-2 text-xs font-bold text-slate-500">Annuler</button>
          </div>
        </div>
      )}

      {/* Classement & confirmation */}
      {["cloturee", "publiee"].includes(phase.statut) && phase.classement?.length > 0 && (
        <div className="mt-4 border-t border-brand/10 pt-4">
          {egalites.length > 0 && phase.statut === "cloturee" && (
            <div data-testid={`phase-egalite-${phase.ordre}`} className="mb-3 flex items-start gap-2 rounded-xl border border-gold/40 bg-gold-glow/50 p-3 text-xs font-semibold text-gold">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Égalité à la frontière de qualification ({egalites.map((r) => `n°${String(r.numero).padStart(2, "0")}`).join(", ")}) — tranchez conformément au règlement.
            </div>
          )}
          <ol className="space-y-1.5">
            {phase.classement.map((r) => (
              <li key={r.candidature_id} className="flex items-center justify-between gap-3 rounded-xl bg-ivory px-4 py-2 text-sm">
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full font-mono text-xs font-bold ${r.rang === 1 ? "bg-gold text-brand-deep" : "bg-white text-slate-500"}`}>{r.rang}</span>
                  {phase.statut === "cloturee" && isAdmin ? (
                    <label className="flex min-w-0 cursor-pointer items-center gap-2">
                      <input type="checkbox" data-testid={`qualifie-${r.numero}`} checked={qualifiees.includes(r.candidature_id)}
                        onChange={(e) => setQualifiees(e.target.checked ? [...qualifiees, r.candidature_id] : qualifiees.filter((x) => x !== r.candidature_id))}
                        className="h-4 w-4 accent-[#0D482B]" />
                      <span className="truncate">n°{String(r.numero).padStart(2, "0")} — {r.prenom} {r.nom}</span>
                    </label>
                  ) : (
                    <span className="truncate">
                      n°{String(r.numero).padStart(2, "0")} — {r.prenom} {r.nom}
                      {r.qualifie && <span className="ml-2 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase text-white">Qualifiée</span>}
                    </span>
                  )}
                </span>
                <span className="shrink-0 font-mono text-xs font-bold text-brand">{r.votes} votes</span>
              </li>
            ))}
          </ol>
          {phase.statut === "cloturee" && isAdmin && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note de validation (égalités…)" data-testid={`phase-note-${phase.ordre}`} className={`${inputCls} flex-1 min-w-[200px]`} />
              <button onClick={confirmer} disabled={qualifiees.length === 0} data-testid={`phase-confirmer-${phase.ordre}`}
                className="rounded-full bg-brand px-5 py-2 text-xs font-bold text-white hover:bg-brand-hover disabled:opacity-50">
                Confirmer les résultats officiels ({qualifiees.length})
              </button>
            </div>
          )}
          {phase.resultats_confirmes && (
            <p className="mt-2 text-xs text-slate-400">Confirmés par {phase.confirmed_by} le {fmtDt(phase.confirmed_at)}{phase.note_confirmation ? ` — ${phase.note_confirmation}` : ""}</p>
          )}
        </div>
      )}
    </div>
  );
};

const PhasesTab = ({ detail, reload, isAdmin }) => {
  const [gagnante, setGagnante] = useState("");
  const finale = [...(detail.phases || [])].reverse().find((p) => p.type !== "proclamation");
  const finalistes = (finale?.classement || []).filter((r) => finale.participants?.includes(r.candidature_id));

  const finaliser = async () => {
    try {
      await api.post(`/admin/concours/${detail.id}/finaliser`, { gagnante_id: gagnante });
      toast.success("Gagnante désignée et notifiée par email !");
      reload();
    } catch (e) { onErr(e); }
  };

  return (
    <div className="space-y-4">
      {detail.phases.map((p) => <PhaseCard key={p.id} eventId={detail.id} phase={p} reload={reload} isAdmin={isAdmin} />)}
      {finale?.statut === "publiee" && !detail.gagnante_id && isAdmin && (
        <div className="rounded-2xl border border-gold/40 bg-gold-glow/30 p-6" data-testid="finaliser-panel">
          <h4 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Crown className="h-5 w-5 text-gold" /> Désigner la gagnante</h4>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select value={gagnante} onChange={(e) => setGagnante(e.target.value)} data-testid="gagnante-select" className={`${inputCls} max-w-xs`}>
              <option value="">Choisir la gagnante…</option>
              {finalistes.map((r) => (
                <option key={r.candidature_id} value={r.candidature_id}>n°{String(r.numero).padStart(2, "0")} — {r.prenom} {r.nom} ({r.votes} votes)</option>
              ))}
            </select>
            <button onClick={finaliser} disabled={!gagnante} data-testid="gagnante-save"
              className="rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-brand-deep hover:bg-gold-warm disabled:opacity-50">
              Couronner la gagnante
            </button>
          </div>
        </div>
      )}
      {detail.gagnante_id && (
        <div className="flex items-center gap-3 rounded-2xl border border-gold/40 bg-gold-glow/30 p-5" data-testid="gagnante-done">
          <Crown className="h-6 w-6 text-gold" />
          <span className="text-sm font-bold text-slate-800">La gagnante a été désignée — concours terminé.</span>
        </div>
      )}
    </div>
  );
};

// ---------- Sous-onglet : Stats & journal ----------
const StatsTab = ({ eventId, isAdmin }) => {
  const [stats, setStats] = useState(null);
  const [votes, setVotes] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [votants, setVotants] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [view, setView] = useState("stats");

  useEffect(() => {
    api.get(`/admin/concours/${eventId}/stats`).then((r) => setStats(r.data)).catch(onErr);
    api.get(`/admin/concours/${eventId}/votes`).then((r) => setVotes(r.data)).catch(onErr);
    api.get(`/admin/concours/${eventId}/audit`).then((r) => setAuditLog(r.data)).catch(onErr);
    api.get(`/admin/concours/${eventId}/email-stats`).then((r) => setEmailStats(r.data)).catch(() => setEmailStats({}));
    if (isAdmin) {
      api.get(`/admin/concours/${eventId}/votants`).then((r) => setVotants(r.data)).catch(() => {});
    }
  }, [eventId, isAdmin]);

  if (!stats) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {[["stats", "Tableau de bord"], ["votes", "Votes détaillés"], ...(isAdmin ? [["votants", "Votants (emails)"]] : []), ["emails", "Emails & codes"], ["audit", "Journal d'audit"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`stats-view-${k}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${view === k ? "bg-brand text-white" : "bg-white text-slate-500 border border-brand/10"}`}>{l}</button>
        ))}
        <a href={`${API}/admin/concours/${eventId}/export.csv`}
          onClick={async (e) => {
            e.preventDefault();
            try {
              const r = await axios.get(`${API}/admin/concours/${eventId}/export.csv`, { headers: authHeaders(), responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url; a.download = "concours_votes.csv"; a.click();
              URL.revokeObjectURL(url);
            } catch (err) { onErr(err); }
          }}
          data-testid="export-csv-btn"
          className="ml-auto inline-flex items-center gap-2 rounded-full border border-brand/25 bg-white px-4 py-2 text-xs font-bold text-brand hover:bg-brand-light">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </a>
      </div>

      {view === "stats" && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: "Candidatures", value: stats.candidatures_total, icon: Users, testid: "stat-candidatures" },
              { label: "Candidates officielles", value: `${stats.officielles}/20`, icon: Trophy, testid: "stat-officielles" },
              { label: "Votes totaux", value: stats.votes_total, icon: Vote, testid: "stat-votes" },
              { label: "Phase en cours", value: stats.phase_en_cours || "—", icon: BarChart3, testid: "stat-phase" },
            ].map((s) => (
              <div key={s.testid} data-testid={s.testid} className="rounded-2xl border border-brand/10 bg-white p-5">
                <s.icon className="h-5 w-5 text-brand" />
                <div className="mt-3 font-display text-2xl font-extrabold text-slate-900">{s.value}</div>
                <div className="mt-1 text-xs font-semibold text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-brand/10 bg-white p-5">
              <h4 className="font-display text-sm font-bold text-slate-900">Candidatures par statut</h4>
              <ul className="mt-3 space-y-2 text-sm">
                {Object.entries(stats.candidatures_par_statut).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="flex items-center gap-2"><Badge style={CAND_STYLE[k]}>{CAND_STATUTS[k]}</Badge></span>
                    <span className="font-mono font-bold">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-brand/10 bg-white p-5">
              <h4 className="font-display text-sm font-bold text-slate-900">Votes par phase & échéances</h4>
              <ul className="mt-3 space-y-2 text-sm">
                {stats.votes_par_phase.map((p) => (
                  <li key={p.phase_id} className="flex items-center justify-between gap-3">
                    <span className="min-w-0 truncate">{p.nom} <span className="text-xs text-slate-400">(fin {fmtDt(p.date_fin)})</span></span>
                    <span className="shrink-0 font-mono font-bold text-brand">{p.votes}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}

      {view === "votes" && (
        <div className="overflow-x-auto rounded-2xl border border-brand/10 bg-white">
          <table className="w-full min-w-[560px] text-left text-sm" data-testid="votes-table">
            <thead className="border-b border-brand/10 bg-ivory text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-5 py-3">Phase</th><th className="px-5 py-3">N°</th><th className="px-5 py-3">Candidate</th><th className="px-5 py-3 text-right">Votes</th></tr>
            </thead>
            <tbody>
              {votes.map((v, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="px-5 py-2.5 text-slate-500">{v.phase_nom}</td>
                  <td className="px-5 py-2.5 font-mono font-bold">{v.numero ? String(v.numero).padStart(2, "0") : "—"}</td>
                  <td className="px-5 py-2.5">{v.nom}</td>
                  <td className="px-5 py-2.5 text-right font-mono font-bold text-brand">{v.votes}</td>
                </tr>
              ))}
              {votes.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">Aucun vote enregistré.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "votants" && isAdmin && (
        <div className="overflow-x-auto rounded-2xl border border-brand/10 bg-white">
          <p className="border-b border-brand/10 bg-gold-glow/40 px-5 py-3 text-xs font-semibold text-gold">
            Adresses email des votants — visibles uniquement par l'administrateur, jamais affichées publiquement.
          </p>
          <table className="w-full min-w-[560px] text-left text-sm" data-testid="votants-table">
            <thead className="border-b border-brand/10 bg-ivory text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-5 py-3">Email</th><th className="px-5 py-3">Vote pour</th><th className="px-5 py-3">Phase</th><th className="px-5 py-3 text-right">Date</th></tr>
            </thead>
            <tbody>
              {votants.map((v, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="px-5 py-2.5 font-medium text-slate-700">{v.email}</td>
                  <td className="px-5 py-2.5">N°{v.numero ? String(v.numero).padStart(2, "0") : "—"} — {v.candidate}</td>
                  <td className="px-5 py-2.5 text-slate-500">{v.phase}</td>
                  <td className="px-5 py-2.5 text-right text-xs text-slate-400">{fmtDt(v.created_at)}</td>
                </tr>
              ))}
              {votants.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">Aucun vote vérifié pour le moment.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "emails" && (
        <div className="space-y-5" data-testid="emails-view">
          {!emailStats ? (
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {[
                  { label: "Codes de vote demandés", value: emailStats.funnel?.codes_demandes ?? 0, testid: "stat-codes-demandes" },
                  { label: "Votes confirmés par code", value: emailStats.funnel?.votes_confirmes ?? 0, testid: "stat-codes-confirmes" },
                  { label: "Taux de confirmation", value: `${emailStats.funnel?.taux_confirmation ?? 0}%`, testid: "stat-taux-confirmation" },
                  { label: "Codes expirés sans vote", value: emailStats.funnel?.expires_sans_vote ?? 0, testid: "stat-codes-expires" },
                ].map((s) => (
                  <div key={s.testid} data-testid={s.testid} className="rounded-2xl border border-brand/10 bg-white p-5">
                    <div className="font-display text-2xl font-extrabold text-slate-900">{s.value}</div>
                    <div className="mt-1 text-xs font-semibold text-slate-500">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-brand/10 bg-white p-5">
                <h4 className="font-display text-sm font-bold text-slate-900">Livraison Resend (emails de code récents)</h4>
                {!emailStats.resend && (
                  <p className="mt-3 text-sm text-slate-400">Aucune donnée — vérifiez la clé Resend ou envoyez un premier code.</p>
                )}
                {emailStats.resend?.erreur && (
                  <p className="mt-3 text-sm text-crimson">Impossible de récupérer les statuts Resend.</p>
                )}
                {emailStats.resend && !emailStats.resend.erreur && (
                  <>
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(emailStats.resend.par_statut || {}).map(([k, v]) => (
                        <li key={k} className="rounded-full bg-ivory px-4 py-1.5 text-xs font-bold text-slate-600">
                          {k} : <span className="font-mono text-brand">{v}</span>
                        </li>
                      ))}
                      {Object.keys(emailStats.resend.par_statut || {}).length === 0 && (
                        <li className="text-sm text-slate-400">Aucun email de code envoyé pour le moment.</li>
                      )}
                    </ul>
                    {isAdmin && emailStats.resend.recents?.length > 0 && (
                      <table className="mt-4 w-full min-w-[480px] text-left text-sm" data-testid="emails-recents-table">
                        <thead className="border-b border-brand/10 text-xs uppercase tracking-wider text-slate-500">
                          <tr><th className="py-2 pr-4">Destinataire</th><th className="py-2 pr-4">Statut</th><th className="py-2 text-right">Date</th></tr>
                        </thead>
                        <tbody>
                          {emailStats.resend.recents.map((r, i) => (
                            <tr key={i} className="border-b border-slate-50">
                              <td className="py-2 pr-4 font-medium text-slate-700">{r.a}</td>
                              <td className="py-2 pr-4"><Badge style={r.statut === "delivered" ? "bg-brand-light text-brand" : r.statut === "bounced" ? "bg-red-50 text-crimson" : "bg-slate-100 text-slate-500"}>{r.statut || "—"}</Badge></td>
                              <td className="py-2 text-right text-xs text-slate-400">{fmtDt(r.date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {view === "audit" && (
        <div className="rounded-2xl border border-brand/10 bg-white p-5" data-testid="audit-list">
          <ul className="space-y-3">
            {auditLog.map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline gap-x-3 border-b border-slate-50 pb-2.5 text-sm last:border-0">
                <span className="font-mono text-xs text-slate-400">{fmtDt(a.created_at)}</span>
                <span className="font-semibold text-brand">{a.acteur}</span>
                <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-slate-500">{a.action}</span>
                <span className="text-slate-600">{a.details}</span>
              </li>
            ))}
            {auditLog.length === 0 && <li className="py-6 text-center text-sm text-slate-400">Aucune action enregistrée.</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------- Composant principal ----------
const SUBTABS = [
  { id: "event", label: "Événement", icon: Trophy },
  { id: "candidatures", label: "Candidatures", icon: Users },
  { id: "phases", label: "Phases & résultats", icon: Vote },
  { id: "config", label: "Bannière & votes", icon: ScrollText },
  { id: "stats", label: "Stats & journal", icon: BarChart3 },
];

export default function ConcoursAdmin() {
  const [events, setEvents] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sub, setSub] = useState("event");
  const isAdmin = getUser()?.role === "admin";

  const loadDetail = useCallback((id) => {
    if (!id) { setDetail(null); return; }
    api.get(`/admin/concours/${id}`).then((r) => setDetail(r.data)).catch(onErr);
  }, []);

  const loadList = useCallback((selectId) => {
    api.get("/admin/concours").then((r) => {
      setEvents(r.data);
      const id = selectId || selectedId || r.data[0]?.id || null;
      setSelectedId(id);
      loadDetail(id);
    }).catch(onErr);
  }, [selectedId, loadDetail]);

  useEffect(() => { loadList(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (id) => { setSelectedId(id); loadDetail(id); };
  const reload = () => { loadDetail(selectedId); };

  if (!events) return <div className="grid place-items-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>;

  return (
    <div data-testid="concours-admin" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        {events.length > 0 && (
          <select value={selectedId || ""} onChange={(e) => select(e.target.value)} data-testid="concours-select"
            className={`${inputCls} max-w-sm`}>
            {events.map((e) => <option key={e.id} value={e.id}>{e.nom} — {EVENT_STATUTS[e.statut]}</option>)}
          </select>
        )}
        {isAdmin && (
          <button onClick={() => { setSelectedId(null); setDetail(null); setSub("event"); }} data-testid="concours-new-btn"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-brand-deep transition-colors hover:bg-gold-warm">
            <Plus className="h-4 w-4" /> Nouvel événement
          </button>
        )}
        {selectedId && (
          <a href="/concours" target="_blank" rel="noopener noreferrer" data-testid="concours-view-public"
            className="inline-flex items-center gap-2 rounded-full border border-brand/25 px-5 py-2.5 text-sm font-bold text-brand hover:bg-brand-light">
            Voir la page publique <ArrowRight className="h-4 w-4" />
          </a>
        )}
      </div>

      {(detail || !selectedId) && (
        <>
          {selectedId && (
            <div className="flex gap-2 overflow-x-auto border-b border-brand/10 pb-3">
              {SUBTABS.map((s) => (
                <button key={s.id} onClick={() => setSub(s.id)} data-testid={`concours-subtab-${s.id}`}
                  className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors ${sub === s.id ? "bg-brand text-white" : "bg-white text-slate-500 border border-brand/10"}`}>
                  <s.icon className="h-3.5 w-3.5" /> {s.label}
                </button>
              ))}
            </div>
          )}
          {sub === "event" && <EventTab detail={detail} reload={reload} reloadList={loadList} isAdmin={isAdmin} />}
          {sub === "candidatures" && detail && <CandidaturesTab eventId={detail.id} isAdmin={isAdmin} />}
          {sub === "phases" && detail && <PhasesTab detail={detail} reload={reload} isAdmin={isAdmin} />}
          {sub === "config" && detail && <ConfigTab detail={detail} reload={reload} isAdmin={isAdmin} />}
          {sub === "stats" && detail && <StatsTab eventId={detail.id} isAdmin={isAdmin} />}
        </>
      )}
      {events.length === 0 && !selectedId && (
        <div className="rounded-2xl border border-dashed border-brand/20 bg-white p-12 text-center">
          <Trophy className="mx-auto h-10 w-10 text-brand/30" />
          <p className="mt-4 text-sm text-slate-500">Aucun événement. Créez votre premier concours pour commencer.</p>
        </div>
      )}
      {selectedId && !detail && <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>}
    </div>
  );
}
